import axios from 'axios';

// 백엔드 기본 URL 설정 (운영 환경에서는 상대 경로/인증서 도메인을 사용하도록 개선)
const client = axios.create({
  // PROD 환경이면 같은 도메인의 /api를 사용 (상대 경로), 아니면 환경변수/로컬 주소 사용
  baseURL: import.meta.env.PROD
    ? ''
    //  : (import.meta.env.VITE_API_URL || 'http://localhost:8100'),
    : (import.meta.env.VITE_API_URL || ''),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 일시적 백엔드 재시작(이 박스의 자동 커밋/리셋 프로세스가 game-back을 종종 재시작함,
// 그 1~2초 창에 502/connection-refused)에 GET 요청이 걸리면 재시도 없이 그대로 실패해
// 월드 부트스트랩(terrain/dongs/region/blocks 등)이 영영 멈추는 사고가 있었음(2026-07-20,
// render-diagnostic stalled 로그로 확인). GET만 지수 백오프로 재시도 — POST/PUT/DELETE는
// 아이템 사용·블록 배치 등 부수효과가 있어 자동 재시도 대상에서 제외.
const RETRYABLE_STATUS = new Set([502, 503, 504]);
// 재시도 예산은 **재시작 창보다 길어야** 의미가 있다.
//   종전 3회/700ms 는 마지막 시도가 4.2초에 끝났는데, 백엔드 콜드 스타트 실측이 약 7초다
//   (import 2.4s + world_block 50,077 0.43s + placed_objects 74,073 0.61s +
//    estate.parcel_blocks 2.26s + 나머지 0.4s + 몬스터 초기화). uvicorn 은 startup 이
//   끝나야 포트를 열기 때문에 그 7초 내내 connection refused → nginx 502 다.
//   즉 종전 값은 **재시작마다 구조적으로 반드시 소진**됐다(로그의 retry=4 가 그 증거).
//   6회 + 개별 상한 4s → 0.8+1.6+2.4+3.2+4+4 = 16초를 덮는다.
const MAX_RETRIES = 6;
const RETRY_DELAY_MS = 800;
const RETRY_DELAY_CAP_MS = 4000;

// 응답 인터셉터 (에러 처리 공통화 가능)
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const isGet = config?.method?.toLowerCase() === 'get';
    const isNetworkError = !error.response; // 커넥션 거부·타임아웃 등 응답 자체가 없음
    const isRetryableStatus = RETRYABLE_STATUS.has(error.response?.status);

    if (config && !config.skipTransientRetry && isGet && (isNetworkError || isRetryableStatus)) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= MAX_RETRIES) {
        const wait = Math.min(RETRY_DELAY_MS * config.__retryCount, RETRY_DELAY_CAP_MS);
        await new Promise((r) => setTimeout(r, wait));
        return client(config);
      }
    }

    // 에러 로그에 **어느 요청인지**를 반드시 적는다.
    //   종전엔 `console.error('API Error:', error)` 였는데, clientLogReporter 는 Error 를
    //   `name: message + stack` 으로만 직렬화한다 → 서버 로그에 남는 건
    //   "Request failed with status code 500" + 번들 심볼(Nt) 뿐이라 **엔드포인트를 알 수 없었다**
    //   (08-06~09 4일치 5xx/4xx 53건이 전부 추적 불가). method·url·status·서버 detail 을 붙인다.
    const where = `${config?.method?.toUpperCase() || '?'} ${config?.baseURL || ''}${config?.url || '?'}`;
    const status = error.response ? error.response.status : 'network';
    let detail = '';
    try {
      const d = error.response?.data;
      detail = typeof d === 'string' ? d.slice(0, 200) : JSON.stringify(d?.detail ?? d ?? '').slice(0, 200);
    } catch { /* noop */ }
    // 저장된 로그인 토큰이 서버에서 거절되면(서명 키 변경·계정 없음·만료) 남은 토큰을 지우고 로그인으로 보낸다.
    if (error.response?.status === 401 && error.response?.data?.detail === '자격 증명이 유효하지 않습니다.'
        && config?.headers?.Authorization && typeof window !== 'undefined'
        && !window.location.pathname.startsWith('/login')) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      localStorage.removeItem('sg_is_admin');
      window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
    const tries = config?.__retryCount ? ` retry=${config.__retryCount}` : '';
    console.error(`API Error: ${status} ${where}${tries}${detail ? ` :: ${detail}` : ''}`, error);
    return Promise.reject(error);
  }
);

// WebSocket URL 생성 헬퍼
export const getWebSocketUrl = (endpoint) => {
  const baseUrl = client.defaults.baseURL;
  // http:// -> ws://, https:// -> wss:// 변환
  const wsBaseUrl = baseUrl.replace(/^http/, 'ws');
  return `${wsBaseUrl}${endpoint}`;
};

export default client;
